/**
 * Habit Tracker Grid — monthly view.
 *
 * 3-column layout:
 *   Col 1 (48px, sticky left-0): emoji rings only — stays when scrolled
 *   Col 2 (170px, scrollable): habit name/details + row labels
 *   Col 3+ (44px each): day checkboxes, stats, charts
 *
 * When user scrolls horizontally, only emoji circles remain visible.
 * Names, labels, and day columns all scroll together.
 */

import React, { useRef, useEffect, useMemo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format, getDaysInMonth, getISODay } from 'date-fns'
import { uz } from 'date-fns/locale'
import { ChevronLeft, ChevronRight, CalendarDays, LayoutList } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useHomeStore } from '@turnifly/shared/stores'
import type { ViewMode } from '@turnifly/shared/stores'
import { useMonthlyHabits, useToggleHabit } from '@turnifly/shared/hooks'
import { formatDateStr, buildLogIndex, calcAllDayStats, countApplicableDays } from '@turnifly/shared/utils'
import type { DayStats } from '@turnifly/shared/utils'
import { HabitCompletionFlow, habitNeedsFlow } from './habit-completion-flow'
import type { PendingToggle } from './habit-completion-flow'
import { DayColumnHeader } from './day-column-header'
import { HabitCard, getHabitCardHeight } from './habit-card'
import { CheckboxCell } from './checkbox-cell'
import { GrowthIndicator } from './growth-row'
import { Skeleton } from '@turnifly/shared/ui'
import type { MonthlyHabitItem, MonthlyHabitsResponse } from '@turnifly/shared/types'

/* ── Helpers ── */

interface WeekGroup { label: number; days: number[] }

function getWeekGroups(year: number, month: number): WeekGroup[] {
  const total = getDaysInMonth(new Date(year, month - 1))
  const groups: WeekGroup[] = []
  let week: number[] = []
  let n = 1
  for (let d = 1; d <= total; d++) {
    week.push(d)
    if (getISODay(new Date(year, month - 1, d)) === 7 || d === total) {
      groups.push({ label: n, days: [...week] })
      week = []
      n++
    }
  }
  return groups
}


/** 0-40% red, 40-60% orange, 60-80% yellow, 80-100% green */
function rateColorHex(pct: number): string {
  if (pct >= 80) return '#22C55E' // green
  if (pct >= 60) return '#EAB308' // yellow
  if (pct >= 40) return '#F97316' // orange
  if (pct > 0) return '#EF4444'   // red
  return 'transparent'
}

function rateColorClass(pct: number, opacity: string): string {
  if (pct >= 80) return `text-[#22C55E]/${opacity}`
  if (pct >= 60) return `text-[#EAB308]/${opacity}`
  if (pct >= 40) return `text-[#F97316]/${opacity}`
  if (pct > 0) return `text-[#EF4444]/${opacity}`
  return 'text-text-hint/15'
}

/* ── Progress ring for emoji col ── */
const RING_SIZE = 36
const RING_STROKE = 2.5
const RING_R = (RING_SIZE - RING_STROKE) / 2
const RING_C = 2 * Math.PI * RING_R

function ringColor(pct: number): string {
  if (pct >= 80) return '#22C55E'
  if (pct >= 60) return '#EAB308'
  if (pct >= 40) return '#F97316'
  if (pct > 0) return '#EF4444'
  return 'rgba(255,255,255,0.06)'
}

/* ── Grid sizing ── */
const EMOJI_W = 48   // sticky emoji column
const INFO_MIN = 185 // scrollable info/label column min width
const COL = 44       // day column

/* ── View mode buttons (mobile only) ── */
const VIEW_MODES: { id: ViewMode; icon: React.ElementType }[] = [
  { id: 'monthly', icon: CalendarDays },
  { id: 'weekly', icon: LayoutList },
]

function colBorder(day: number, year: number, month: number): string {
  const sun = getISODay(new Date(year, month - 1, day)) === 7
  const last = day === getDaysInMonth(new Date(year, month - 1))
  return sun && !last ? 'border-r border-r-white/[0.12] z-[1]' : 'border-r border-r-white/[0.04]'
}

const LABEL_CLS = 'text-[11px] font-medium uppercase tracking-wide text-center w-full'
const LABEL_COLOR = '#768c9e'
const BG_PAGE = 'bg-[var(--color-bg)]' // same as page background — transparent feel
/** ~3% lighter than BG_PAGE — #0e1621 mixed with 3% white ≈ #131b27 */
const BG_TINTED = { backgroundColor: '#131b27' }

/* ═══ MAIN ═══ */

export const HabitTrackerGrid: React.FC = () => {
  const { t, i18n } = useTranslation()
  const userId = useAuthStore((s) => s.user?.id)
  const gender = useAuthStore((s) => s.user?.gender === 'female' ? 'female' : 'male') as 'male' | 'female'
  const store = useHomeStore()
  const { year, month, viewMode } = store
  const scrollRef = useRef<HTMLDivElement>(null)
  const toggleMutation = useToggleHabit(year, month)

  const totalDays = getDaysInMonth(new Date(year, month - 1))
  const days = useMemo(() => Array.from({ length: totalDays }, (_, i) => i + 1), [totalDays])
  const weeks = useMemo(() => getWeekGroups(year, month), [year, month])

  const todayDate = useMemo(() => {
    const n = new Date()
    return { day: n.getDate(), month: n.getMonth() + 1, year: n.getFullYear(), dateStr: formatDateStr(n.getFullYear(), n.getMonth() + 1, n.getDate()) }
  }, [])
  const isCurMonth = year === todayDate.year && month === todayDate.month

  const { data, isLoading } = useMonthlyHabits(userId, year, month)
  const habits = useMemo((): MonthlyHabitItem[] => {
    if (!data) return []
    const raw = (data as MonthlyHabitsResponse).habits ?? []
    // Sort: habits with time_start ASC first, then without time at the end
    return [...raw].sort((a, b) => {
      const aTime = a.time_start
      const bTime = b.time_start
      if (aTime && bTime) return aTime.localeCompare(bTime)
      if (aTime && !bTime) return -1
      if (!aTime && bTime) return 1
      return (a.sort_order ?? 0) - (b.sort_order ?? 0)
    })
  }, [data])

  const logIndex = useMemo(() => buildLogIndex(habits), [habits])
  const dayStats = useMemo(() => calcAllDayStats(habits, days, year, month, logIndex), [habits, days, year, month, logIndex])
  const maxDay = isCurMonth ? Math.min(todayDate.day, totalDays) : totalDays

  // Completion flow state — for habits that need timer/value/proof
  const [pendingToggle, setPendingToggle] = useState<PendingToggle | null>(null)

  const handleToggle = useCallback((habitId: string, logDate: string, completed: boolean): void => {
    if (!userId) return

    // Unchecking — always immediate, no modal
    if (!completed) {
      toggleMutation.mutate({ userId, habitId, logDate, completed: false })
      return
    }

    // Checking — find habit and check if it needs flow
    const habit = habits.find((h) => h.id === habitId)
    if (!habit) return

    if (habitNeedsFlow(habit)) {
      setPendingToggle({ habit, logDate })
    } else {
      toggleMutation.mutate({ userId, habitId, logDate, completed: true })
    }
  }, [userId, toggleMutation, habits])

  const handleFlowComplete = useCallback((habitId: string, logDate: string, data: { value: number | null; proofUrls: Array<{ url: string; type: 'image' | 'video' }>; proofText: string | null }): void => {
    if (!userId) return
    toggleMutation.mutate({ userId, habitId, logDate, completed: true, value: data.value, proofUrls: data.proofUrls, proofText: data.proofText })
    setPendingToggle(null)
  }, [userId, toggleMutation])

  const handleFlowCancel = useCallback((): void => {
    setPendingToggle(null)
  }, [])

  useEffect(() => {
    if (!isCurMonth || !scrollRef.current) return
    const cw = scrollRef.current.clientWidth
    const target = Math.max(0, INFO_MIN + (todayDate.day - 1) * COL - cw / 2 + COL / 2)
    const timer = setTimeout(() => scrollRef.current?.scrollTo({ left: target, behavior: 'smooth' }), 200)
    return (): void => clearTimeout(timer)
  }, [isCurMonth, year, month, todayDate.day])

  const isToday = (d: number): boolean => isCurMonth && d === todayDate.day
  const todayBg = 'bg-primary/[0.1]'
  const locale = i18n.language === 'uz' ? uz : undefined
  const monthLabel = format(new Date(year, month - 1, 1), 'LLLL yyyy', { locale })

  if (isLoading) {
    return (
      <div className="rounded-2xl glass p-4 flex flex-col gap-3">
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-14 w-full rounded-lg" />
      </div>
    )
  }

  if (habits.length === 0) {
    return (
      <div className="rounded-2xl glass overflow-hidden">
        <GridHeader monthLabel={monthLabel} viewMode={viewMode} onMode={store.setViewMode} onPrev={store.prevMonth} onNext={store.nextMonth} onToday={store.goToToday} />
        <div className="min-h-[200px] flex flex-col items-center justify-center text-center p-8 gap-3">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary text-2xl">+</div>
          <h4 className="text-text font-display font-semibold text-base">{t('home.noHabits')}</h4>
          <p className="text-sm text-text-hint max-w-[220px]">{t('home.noHabitsDesc')}</p>
        </div>
      </div>
    )
  }

  const maxRate = 100

  return (
    <div className="rounded-2xl glass overflow-hidden habit-grid-wrapper">
      <GridHeader monthLabel={monthLabel} viewMode={viewMode} onMode={store.setViewMode} onPrev={store.prevMonth} onNext={store.nextMonth} onToday={store.goToToday} />

      <div className="flex">
        {/* ━━━ STICKY EMOJI COLUMN (left, never scrolls) ━━━ */}
        {/* No border-r between sticky and info — they look like one piece */}
        <div className="shrink-0 z-20" style={{ width: EMOJI_W }}>
          {/* Growth row — page bg */}
          <div className={`h-20 border-b border-b-white/[0.06] ${BG_PAGE}`} />
          {/* Week label row */}
          <div className={`h-7 border-b border-b-white/[0.06] ${BG_PAGE}`} />
          {/* Day header row */}
          <div className={`h-11 border-b border-b-white/[0.08] ${BG_PAGE}`} />
          {/* Habit emoji rows — card bg (glass-solid) */}
          {habits.map((habit, idx) => {
            const rowH = getHabitCardHeight(habit)
            const completedCount = habit.logs.filter((l) => l.completed).length
            const applicableDays = countApplicableDays(habit, year, month)
            const rate = applicableDays > 0 ? Math.min(completedCount / applicableDays, 1) : 0
            const pct = Math.round(rate * 100)
            const color = ringColor(pct)
            const offset = RING_C * (1 - rate)
            const firstPad = idx === 0 ? 3 : 0
            return (
              <div
                key={`e-${habit.id}`}
                className={`flex items-center justify-center ${BG_PAGE}`}
                style={{ height: rowH + firstPad, paddingTop: firstPad || undefined, borderBottom: '3px solid var(--color-bg)' }}
              >
                <div className="relative" style={{ width: RING_SIZE, height: RING_SIZE }}>
                  <svg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} className="absolute inset-0 -rotate-90">
                    <circle cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={RING_STROKE} />
                    {pct > 0 && (
                      <circle cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_R} fill="none" stroke={color} strokeWidth={RING_STROKE} strokeLinecap="round" strokeDasharray={RING_C} strokeDashoffset={offset} className="transition-all duration-700 ease-out" />
                    )}
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[15px] leading-none">{habit.icon}</span>
                  </div>
                  {habit.challenge_id && (
                    <span className="absolute -bottom-0.5 -right-0.5 text-[8px] leading-none">🏆</span>
                  )}
                </div>
              </div>
            )
          })}
          {/* Stats rows */}
          <div className={`h-7 border-t border-t-white/[0.08] ${BG_PAGE}`} />
          <div className={`h-6 ${BG_PAGE}`} />
          <div className={`h-20 border-t border-t-white/[0.04] ${BG_PAGE}`} />
          <div className={`h-7 border-t border-t-white/[0.06] ${BG_PAGE}`} />
          <div className={`h-24 border-t border-t-white/[0.06] ${BG_PAGE}`} />
        </div>

        {/* ━━━ SCROLLABLE AREA (info col + day columns) ━━━ */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-x-auto overflow-y-hidden overscroll-x-contain"
          style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}
        >
          <div className="grid" style={{ gridTemplateColumns: `minmax(${INFO_MIN}px, 1fr) repeat(${totalDays}, ${COL}px)`, minWidth: `${INFO_MIN + totalDays * COL}px` }}>

            {/* ── Growth row — label vertically centered, page bg ── */}
            <div className={`h-20 flex items-center justify-center border-b border-b-white/[0.06] border-r border-r-white/[0.06] ${BG_PAGE}`}>
              <span className={LABEL_CLS} style={{ color: LABEL_COLOR }}>
                {gender === 'female' ? t('home.gardenLabel') : t('home.orchardLabel')}
              </span>
            </div>
            {days.map((day, i) => (
              <div key={`g-${day}`} className="h-20 border-b border-b-white/[0.06]" style={{ backgroundColor: '#001f2e' }}>
                <GrowthIndicator day={day} year={year} month={month} completionRate={dayStats[i].rate} gender={gender} />
              </div>
            ))}

            {/* ── Week labels — "Haftalar" in info col ── */}
            <div className={`h-7 flex items-center justify-center border-b border-b-white/[0.06] border-r border-r-white/[0.06] ${BG_PAGE}`}>
              <span className={LABEL_CLS} style={{ color: LABEL_COLOR }}>{t('home.weeks')}</span>
            </div>
            {weeks.map((w) => (
              <div key={`wh-${w.label}`} className="h-7 flex items-center justify-center text-[10px] font-bold uppercase tracking-[0.1em] border-b border-b-white/[0.06] border-r border-r-white/[0.06]" style={{ gridColumn: `span ${w.days.length}`, color: LABEL_COLOR, ...BG_TINTED }}>
                {t('home.weekLabel', { n: w.label })}
              </div>
            ))}

            {/* ── Day headers — "ODATLAR/SANA" in info col ── */}
            <div className={`h-11 flex items-center justify-center border-b border-b-white/[0.08] border-r border-r-white/[0.06] ${BG_PAGE}`}>
              <span className={LABEL_CLS} style={{ color: LABEL_COLOR }}>
                {t('home.habitsDate')}
              </span>
            </div>
            {days.map((day) => (
              <div key={`dh-${day}`} className={`border-b border-b-white/[0.08] ${colBorder(day, year, month)} ${isToday(day) ? todayBg : ''}`} style={isToday(day) ? undefined : BG_TINTED}>
                <DayColumnHeader day={day} year={year} month={month} todayDateStr={todayDate.dateStr} />
              </div>
            ))}

            {/* ── Habit rows ── */}
            {habits.map((habit, idx) => {
              const rowH = getHabitCardHeight(habit)
              const firstPad = idx === 0 ? 3 : 0
              return (
                <React.Fragment key={habit.id}>
                  <div className={`border-r border-r-white/[0.06] ${BG_PAGE}`} style={firstPad ? { paddingTop: firstPad } : undefined}>
                    <HabitCard habit={habit} />
                  </div>
                  {/* Day checkboxes */}
                  {days.map((day) => (
                    <div key={`${habit.id}-${day}`} className={`border-b border-b-white/[0.04] ${colBorder(day, year, month)} ${isToday(day) ? todayBg : ''}`} style={{ height: rowH + firstPad, paddingTop: firstPad || undefined }}>
                      <CheckboxCell habit={habit} day={day} year={year} month={month} todayDateStr={todayDate.dateStr} onToggle={handleToggle} />
                    </div>
                  ))}
                </React.Fragment>
              )
            })}

            {/* ── Daily count ── */}
            <div className={`h-7 flex items-center justify-center border-t border-t-white/[0.08] border-r border-r-white/[0.06] ${BG_PAGE}`}>
              <span className={LABEL_CLS} style={{ color: LABEL_COLOR }}>{t('home.dailyDone')}</span>
            </div>
            {days.map((day, i) => {
              const s = dayStats[i]
              return (
                <div key={`cnt-${day}`} className={`h-7 flex items-center justify-center border-t border-t-white/[0.08] text-[11px] font-bold tabular-nums ${rateColorClass(s.rate, '80')} ${colBorder(day, year, month)} ${isToday(day) ? todayBg : ''}`} style={isToday(day) ? undefined : BG_TINTED}>
                  {s.completed}
                </div>
              )
            })}

            {/* ── Daily percent ── */}
            <div className={`h-6 flex items-center justify-center border-r border-r-white/[0.06] ${BG_PAGE}`}>
              <span className={LABEL_CLS} style={{ color: LABEL_COLOR }}>{t('home.dailyPercent')}</span>
            </div>
            {days.map((day, i) => {
              const s = dayStats[i]
              return (
                <div key={`pct-${day}`} className={`h-6 flex items-center justify-center text-[9px] font-semibold tabular-nums ${rateColorClass(s.rate, '60')} ${colBorder(day, year, month)} ${isToday(day) ? todayBg : ''}`} style={isToday(day) ? undefined : BG_TINTED}>
                  {s.rate}%
                </div>
              )
            })}

            {/* ── Mini bars ── */}
            <div className={`h-20 border-t border-t-white/[0.04] border-r border-r-white/[0.06] ${BG_PAGE}`} />
            {days.map((day, i) => {
              const s = dayStats[i]
              const barH = Math.round((s.rate / 100) * 70)
              return (
                <div key={`bar-${day}`} className={`h-20 flex items-end justify-center border-t border-t-white/[0.04] ${getISODay(new Date(year, month - 1, day)) === 7 && day !== getDaysInMonth(new Date(year, month - 1)) ? 'border-r border-r-white/[0.12]' : ''}`} style={{ paddingTop: '3px' }}>
                  {s.rate > 0 && <div className="w-5 rounded-t-sm transition-all duration-500 ease-out" style={{ height: `${barH}px`, backgroundColor: rateColorHex(s.rate), opacity: 0.85 }} />}
                </div>
              )
            })}

            {/* ── Weekly progress ── */}
            <div className={`h-7 flex items-center justify-center border-t border-t-white/[0.06] border-r border-r-white/[0.06] ${BG_PAGE}`}>
              <span className={LABEL_CLS} style={{ color: LABEL_COLOR }}>{t('home.weeklyRate')}</span>
            </div>
            {weeks.map((w) => {
              let wC = 0, wT = 0
              for (const d of w.days) { const s = dayStats[d - 1]; wC += s.completed; wT += s.total }
              const wr = wT > 0 ? Math.round((wC / wT) * 100) : 0
              const barColor = rateColorHex(wr)
              return (
                <div key={`wk-${w.label}`} className="h-7 flex items-center gap-1.5 px-1.5 border-t border-t-white/[0.06] border-r border-r-white/[0.06]" style={{ gridColumn: `span ${w.days.length}`, ...BG_TINTED }}>
                  <div className="flex-1 h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${wr}%`, backgroundColor: barColor, opacity: 0.85 }} />
                  </div>
                  <span className="text-[9px] font-bold tabular-nums shrink-0" style={{ color: LABEL_COLOR, opacity: wr > 0 ? 1 : 0.3 }}>{wr}%</span>
                </div>
              )
            })}

            {/* ── Line chart — completion donut in label cell ── */}
            <div className={`h-24 flex items-center justify-center border-t border-t-white/[0.06] border-r border-r-white/[0.06] ${BG_PAGE}`}>
              {(() => {
                // mc = completed checkboxes, mt = total applicable checkboxes
                // Calculated from habits+logs (daily_stats only has days with activity)
                let mc = 0, mt = 0
                for (const d of days) {
                  const ds = dayStats[d - 1]
                  mt += ds.total
                  mc += ds.completed
                }
                const mr = mt > 0 ? Math.round((mc / mt) * 100) : 0
                const donutColor = rateColorHex(mr)
                const SZ = 72, SW = 5, r = (SZ - SW) / 2, circ = 2 * Math.PI * r
                return (
                  <div className="relative" style={{ width: SZ, height: SZ }}>
                    <svg width={SZ} height={SZ} viewBox={`0 0 ${SZ} ${SZ}`} className="absolute inset-0 -rotate-90">
                      <circle cx={SZ / 2} cy={SZ / 2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={SW} />
                      {mr > 0 && <circle cx={SZ / 2} cy={SZ / 2} r={r} fill="none" stroke={donutColor} strokeWidth={SW} strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - mr / 100)} />}
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-[13px] font-bold tabular-nums leading-none" style={{ color: donutColor }}>{mr}%</span>
                      <span className="text-[8px] tabular-nums mt-0.5" style={{ color: LABEL_COLOR }}>{mc}/{mt}</span>
                    </div>
                  </div>
                )
              })()}
            </div>
            <div className="h-24 border-t border-t-white/[0.06]" style={{ gridColumn: `2 / -1` }}>
              <svg width={totalDays * COL} height={96} viewBox={`0 0 ${totalDays * COL} 96`} style={{ display: 'block', minWidth: totalDays * COL }}>
                <defs>
                  <linearGradient id="lineG" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#86EFAC" />
                    <stop offset="50%" stopColor="#2DD4BF" />
                    <stop offset="100%" stopColor="#14B8A6" />
                  </linearGradient>
                  <linearGradient id="areaG" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#86EFAC" />
                    <stop offset="50%" stopColor="#2DD4BF" />
                    <stop offset="100%" stopColor="#14B8A6" />
                  </linearGradient>
                  <linearGradient id="areaFade" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="white" stopOpacity="0.45" />
                    <stop offset="100%" stopColor="white" stopOpacity="0.05" />
                  </linearGradient>
                  <mask id="areaMask">
                    <rect width="100%" height="100%" fill="url(#areaFade)" />
                  </mask>
                </defs>
                <path d={smoothArea(dayStats, totalDays, COL, 96, maxRate)} fill="url(#areaG)" mask="url(#areaMask)" />
                <path d={smoothLine(dayStats, totalDays, COL, 96, maxRate)} fill="none" stroke="url(#lineG)" strokeWidth="2" strokeLinecap="round" />
                {isCurMonth && (() => {
                  const ti = todayDate.day - 1
                  const x = (ti + 0.5) * COL
                  const y = 10 + 76 - (dayStats[ti]?.rate ?? 0) / maxRate * 76
                  return <circle cx={x} cy={y} r="4" fill="#14B8A6" stroke="var(--color-bg)" strokeWidth="2" />
                })()}
              </svg>
            </div>
          </div>
        </div>
      </div>

      <SummaryBar dayStats={dayStats} maxDay={maxDay} />

      <HabitCompletionFlow pending={pendingToggle} onComplete={handleFlowComplete} onCancel={handleFlowCancel} />
    </div>
  )
}

/* ── Grid Header ── */

interface GridHeaderProps { monthLabel: string; viewMode: ViewMode; onMode: (m: ViewMode) => void; onPrev: () => void; onNext: () => void; onToday: () => void }

const GridHeader: React.FC<GridHeaderProps> = ({ monthLabel, viewMode, onMode, onPrev, onNext, onToday }) => (
  <div className="relative flex items-center justify-between lg:justify-center px-4 py-2.5 bg-glass-solid border-b border-white/[0.06]">
    <div className="flex items-center gap-1.5">
      <button onClick={onPrev} className="p-1.5 rounded-lg text-text-hint/40 hover:text-text hover:bg-white/[0.04] transition-colors tap-highlight-transparent"><ChevronLeft size={16} /></button>
      <button onClick={onToday} className="font-display font-bold text-[14px] text-text capitalize px-2 py-1 rounded-lg hover:bg-white/[0.04] transition-colors">{monthLabel}</button>
      <button onClick={onNext} className="p-1.5 rounded-lg text-text-hint/40 hover:text-text hover:bg-white/[0.04] transition-colors tap-highlight-transparent"><ChevronRight size={16} /></button>
    </div>
    <div className="flex items-center gap-0.5 lg:hidden">
      {VIEW_MODES.map((m) => {
        const I = m.icon; const active = viewMode === m.id
        return <button key={m.id} onClick={() => onMode(m.id)} className={`p-2 rounded-lg transition-colors tap-highlight-transparent ${active ? 'text-primary bg-primary/8' : 'text-text-hint/30 hover:text-text-hint/60'}`}><I size={15} /></button>
      })}
    </div>
  </div>
)

/* ── SVG ── */

/** Data points at column centers */
function svgPts(stats: DayStats[], n: number, cw: number, h: number, mr: number): { x: number; y: number }[] {
  const py = 10; const ch = h - py * 2
  return stats.slice(0, n).map((s, i) => ({
    x: (i + 0.5) * cw,
    y: py + ch - (s.rate / mr) * ch,
  }))
}

/** Extended points: data points at column centers + edge points at x=0 and x=totalW */
function extendedPts(stats: DayStats[], n: number, cw: number, h: number, mr: number): { x: number; y: number }[] {
  const pts = svgPts(stats, n, cw, h, mr)
  if (pts.length === 0) return pts
  return [{ x: 0, y: pts[0].y }, ...pts, { x: n * cw, y: pts[pts.length - 1].y }]
}

function smoothLine(stats: DayStats[], n: number, cw: number, h: number, mr: number): string {
  const p = extendedPts(stats, n, cw, h, mr); if (p.length < 2) return ''
  let d = `M${p[0].x},${p[0].y}`
  for (let i = 1; i < p.length; i++) { const t = (p[i].x - p[i - 1].x) * 0.35; d += ` C${p[i - 1].x + t},${p[i - 1].y} ${p[i].x - t},${p[i].y} ${p[i].x},${p[i].y}` }
  return d
}

function smoothArea(stats: DayStats[], n: number, cw: number, h: number, mr: number): string {
  const p = extendedPts(stats, n, cw, h, mr); if (p.length < 2) return ''
  let d = `M${p[0].x},${h} L${p[0].x},${p[0].y}`
  for (let i = 1; i < p.length; i++) { const t = (p[i].x - p[i - 1].x) * 0.35; d += ` C${p[i - 1].x + t},${p[i - 1].y} ${p[i].x - t},${p[i].y} ${p[i].x},${p[i].y}` }
  d += ` L${p[p.length - 1].x},${h} Z`; return d
}

/* ── Summary Bar ── */

const LEGEND_ITEMS = [
  { min: 0, max: 39, color: '#EF4444', labelKey: 'home.legendBad' },
  { min: 40, max: 59, color: '#F97316', labelKey: 'home.legendAverage' },
  { min: 60, max: 79, color: '#EAB308', labelKey: 'home.legendGood' },
  { min: 80, max: 100, color: '#22C55E', labelKey: 'home.legendGreat' },
] as const

const SummaryBar: React.FC<{ dayStats: DayStats[]; maxDay: number }> = ({ }) => {
  const { t } = useTranslation()
  return (
    <div className="px-3 py-2.5 border-t border-t-white/[0.06] flex items-center justify-center gap-4 bg-glass-solid">
      {LEGEND_ITEMS.map((item) => {
        return (
          <div key={item.labelKey} className="flex items-center gap-2">
            <div
              className="w-2.5 h-2.5 rounded-[3px] shrink-0"
              style={{ backgroundColor: item.color, opacity: 0.85 }}
            />
            <span
              className="text-[10px] font-semibold"
              style={{ color: LABEL_COLOR }}
            >
              {t(item.labelKey)}
            </span>
          </div>
        )
      })}
    </div>
  )
}
